import {
  BlockIOPSMap,
  BlockMouseDownEvent,
  BlockPositionMap,
  ContentIOPSCountMap,
  ContentTypes,
  IOPS,
  OperationTypes,
  ROOT_BLOCK_ID,
  blockConfigs,
  zeroIOPS,
  Block,
  QUEUE_MAX_STORAGE,
  multiplyIOPS,
} from "./block";
import { BlockTypes } from "./block-types";
import { ConnectPointClickEvent, ConnectorMap, ReactFlowHandleClickEvent } from "./connectors";
import { Edge, Node } from "reactflow"
import { getNextIOPSScale, TrafficPatterns, TrafficRoutes, defaultTrafficRoutes, getPeriodicTPS, computeIOPSFromRoutes } from "./traffic-patterns";

// Every game tick (~ 5 secs) = 1 hour
export const LEVEL_UP_INTERVAL_SEC = 30;
export const INITIAL_REQUEST_COUNT_ROOT = 10;


const DEFAULT_CPM = 7; //Earnings per 1000 requests

export interface PublicSentiment {
  sentiment: number;
  recoveryTimeInSecs: number;
  lastUpdatedInSecs: number;
}

export interface GameState {
  blocks: BlockPositionMap;
  nodes: Node[];
  edges: Edge[];
  connectors: ConnectorMap;
  blockIOPS: BlockIOPSMap;
  connectionStartBlock: ConnectPointClickEvent | null;
  connectionStartNode: ReactFlowHandleClickEvent | null;
  selectedBlock: string | null;
  draggingBlock: BlockMouseDownEvent | null;
  timeElapsedInSec: number;
  level: number;
  isRunning: boolean;
  isStarted: boolean;
  isOver: boolean;
  accountBalance: number;
  initialRootIOPS: IOPS;
  trafficPattern: TrafficPatterns;
  trafficRoutes: TrafficRoutes;
  settingsModalOpenForBlockId: string | null;
  isDraggingNode: boolean;
  minTPS: number;
  maxTPS: number;
  isIopsConfigModalOpen?: boolean;
  isPaletteCollapsed?: boolean;
}

interface NodeResult {
  percentSuccess: number;
  gameState: GameState;
}

export interface IOPSSummary {
  inputTotal: number;
  outputTotal: number;
  percentSuccess: number;
}

export interface GameStatus {
  isRunning: boolean;
  isStarted: boolean;
  isOver: boolean;
}

const initialX = typeof window !== 'undefined' ? window.innerWidth / 2 - 50 : 500;
const initialY = typeof window !== 'undefined' ? window.innerHeight / 2 - 50 : 300;

export const initialGameState: GameState = {
  nodes: [{ id: 'root', type: 'gameBlock', data: { label: 'User', blockType: BlockTypes.USER }, position: { x: initialX, y: initialY } }],
  edges: [],
  blocks: {
    [`${ROOT_BLOCK_ID}`]: {
      top: initialY,
      left: initialX,
      id: ROOT_BLOCK_ID,
      width: 100,
      height: 100,
      blockType: BlockTypes.USER,
      inputCapacity: blockConfigs[BlockTypes.USER].inputCapacity,
      processingCapacity: blockConfigs[BlockTypes.USER].processingCapacity,
    },
  },
  connectors: {
  },
  blockIOPS: {
    [`${ROOT_BLOCK_ID}`]: {
      blockID: ROOT_BLOCK_ID,
      inputIOPS: { total: 0, byOperationType: {} },
      outputIOPS: {
        total: INITIAL_REQUEST_COUNT_ROOT,
        byOperationType: {
          [`${OperationTypes.READ}`]: {
            total: 2,
            byContentType: {
              [`${ContentTypes.STATIC}`]: 1,
              [`${ContentTypes.DYNAMIC}`]: 1,
              [`${ContentTypes.STATIC_MEDIA}`]: 0,
            },
          },

          [`${OperationTypes.WRITE}`]: {
            total: 1,
            byContentType: {
              [`${ContentTypes.DYNAMIC}`]: 1,
              [`${ContentTypes.STATIC}`]: 0,
              [`${ContentTypes.STATIC_MEDIA}`]: 0,
            },
          },
        },
      },
      percentageSuccess: 100
    },
  },
  timeElapsedInSec: 0,
  level: 1,
  isRunning: false,
  isStarted: false,
  isOver: false,
  connectionStartBlock: null,
  connectionStartNode: null,
  selectedBlock: null,
  draggingBlock: null,
  accountBalance: 270,
  initialRootIOPS: {
    total: INITIAL_REQUEST_COUNT_ROOT,
    byOperationType: {
      [`${OperationTypes.READ}`]: {
        total: 6,
        byContentType: {
          [`${ContentTypes.STATIC}`]: 3,
          [`${ContentTypes.DYNAMIC}`]: 3,
          [`${ContentTypes.STATIC_MEDIA}`]: 0,
        },
      },

      [`${OperationTypes.WRITE}`]: {
        total: 4,
        byContentType: {
          [`${ContentTypes.DYNAMIC}`]: 4,
          [`${ContentTypes.STATIC}`]: 0,
          [`${ContentTypes.STATIC_MEDIA}`]: 0,
        },
      },
    },
  },
  trafficPattern: TrafficPatterns.STEADY,
  trafficRoutes: defaultTrafficRoutes,
  settingsModalOpenForBlockId: null,
  isDraggingNode: false,
  minTPS: 10,
  maxTPS: 100,
  isIopsConfigModalOpen: false,
  isPaletteCollapsed: false,
};

export function getNextCanvasState(currentGameState: GameState) {
  const currentBlock = currentGameState.blockIOPS[ROOT_BLOCK_ID];
  if (!currentBlock) return currentGameState;

  const computedLevel = Math.round(currentGameState.timeElapsedInSec / LEVEL_UP_INTERVAL_SEC);
  const rootIOPSTotal = currentGameState.blockIOPS[`${ROOT_BLOCK_ID}`].outputIOPS.total;
  const lastPercentSuccess = currentGameState.blockIOPS[`${ROOT_BLOCK_ID}`].percentageSuccess;

  const minLimit = currentGameState.minTPS !== undefined ? currentGameState.minTPS : 10;
  const maxLimit = currentGameState.maxTPS !== undefined ? currentGameState.maxTPS : 100;

  let nextTotalTPS = rootIOPSTotal;

  if (currentGameState.trafficPattern === TrafficPatterns.RAMP) {
    // Starts at 0 TPS and ramps linearly to the desired Max TPS over 60 seconds (RAMP_DURATION)
    const rampDur = 60; // 60 seconds
    nextTotalTPS = Math.min(maxLimit, (maxLimit * currentGameState.timeElapsedInSec) / rampDur);
  } else if (currentGameState.trafficPattern === TrafficPatterns.PERIODIC) {
    // Oscillates between minTPS and maxTPS using getPeriodicTPS
    nextTotalTPS = getPeriodicTPS(currentGameState.timeElapsedInSec, minLimit, maxLimit);
  } else {
    // Existing patterns use getNextIOPSScale to scale the previous total
    const nextIOPSScale = getNextIOPSScale(currentGameState.trafficPattern || TrafficPatterns.STEADY, rootIOPSTotal, lastPercentSuccess);
    nextTotalTPS = rootIOPSTotal * nextIOPSScale;
  }

  // Enforce min and max limits for all traffic patterns
  nextTotalTPS = Math.max(minLimit, Math.min(maxLimit, nextTotalTPS));

  let rootOutputIOPS: IOPS;
  if (currentGameState.trafficPattern === TrafficPatterns.RAMP || currentGameState.trafficPattern === TrafficPatterns.PERIODIC) {
    rootOutputIOPS = computeIOPSFromRoutes(nextTotalTPS, currentGameState.trafficRoutes);
  } else {
    const actualScale = rootIOPSTotal > 0 ? nextTotalTPS / rootIOPSTotal : 1;
    rootOutputIOPS = getNextRootIOPS(
      currentGameState.level,
      computedLevel,
      currentGameState.blockIOPS[`${ROOT_BLOCK_ID}`].outputIOPS,
      actualScale,
      lastPercentSuccess
    );
  }

  let finalState = computeNextIOPSForBlock(ROOT_BLOCK_ID, { ...currentGameState }, rootOutputIOPS, new Set<string>()).gameState
  finalState.level = computedLevel;

  const cpm = getEarnedCPM(finalState.blockIOPS[`${ROOT_BLOCK_ID}`].percentageSuccess / 100 * finalState.blockIOPS[`${ROOT_BLOCK_ID}`].outputIOPS.total)
  const cycleCost = getTotalCostPerCycle(finalState);
  finalState.accountBalance = finalState.accountBalance + cpm - cycleCost;

  return finalState
}

//recursively computes the next IOPS values for each node in the block tree
function computeNextIOPSForBlock(currentBlockID: string,
  computedState: GameState, parentIOPS: IOPS, seenBlocks: Set<string>): NodeResult {
  const currentBlock = computedState.blockIOPS[currentBlockID];
  if (!currentBlock) return { percentSuccess: 100, gameState: computedState };

  let totalInputIOPS: IOPS = zeroIOPS();

  //if the block has not been seen, overwrite the inputIOPS with the parentIOPS
  if (!seenBlocks.has(currentBlockID)) {
    totalInputIOPS = parentIOPS;
    seenBlocks.add(currentBlockID);
  }
  else {
    //otherwise add the parentIOPS to the existing inputIOPS
    totalInputIOPS = sumIOPS(computedState.blockIOPS[currentBlockID].inputIOPS, parentIOPS);
  }

  //update the inputIOPS for this block
  computedState = { ...computedState, blockIOPS: { ...computedState.blockIOPS, [currentBlockID]: { ...computedState.blockIOPS[currentBlockID], inputIOPS: totalInputIOPS } } }

  // For Passthrough blocks, set hasChildren flag based on edge count
  if (computedState.blocks[currentBlockID]?.blockType === BlockTypes.PASSTHROUGH) {
    const childCount = computedState.edges.filter((edge) => edge.source === currentBlockID).length;
    computedState.blocks[currentBlockID] = {
      ...computedState.blocks[currentBlockID],
      dynamicProperties: {
        ...computedState.blocks[currentBlockID].dynamicProperties,
        hasChildren: childCount > 0,
      },
    };
  }

  //run block side effects
  const sideEffects = blockConfigs[computedState.blocks[currentBlockID]?.blockType]?.blockSideEffects;
  if (sideEffects) {
    computedState.blocks[currentBlockID] = sideEffects(computedState.blocks[currentBlockID], totalInputIOPS);
  }

  //get processingCapacity for this block
  const processingCapacity = computedState.blocks[currentBlockID].processingCapacity

  let inputIOPS = totalInputIOPS

  //subtract the processing capacity from the total IOPS
  const remainingIOPS = diffIOPS(inputIOPS, processingCapacity);
  let totalStoredIOPS = zeroIOPS();

  let availableToDistribute = remainingIOPS;

  //loop over all output connectors for this block get a list of next blocks
  const nextBlockKeys = computedState.edges.filter((edge) => edge.source === currentBlockID).map((edge) => edge.target)
  const nextBlocks = nextBlockKeys.map((blockID: string) => computedState.blocks[blockID]);

  if (computedState.blocks[currentBlockID].blockType === BlockTypes.QUEUE) {
    const stored = computedState.blocks[currentBlockID].dynamicProperties?.queueState || zeroIOPS();

    // Add new input to stored
    let newTotalStored = sumIOPS(stored, availableToDistribute);

    const maxTotalCapacityBlock = distributeIOPSToBlocks(nextBlocks, newTotalStored);
    const maxTotalCapacity = Object.values(maxTotalCapacityBlock).reduce((total, blockIOPS) => {
      return sumIOPS(total, blockIOPS);
    }, zeroIOPS());

    //set availableToDistribute to the max that children can accept
    availableToDistribute = maxTotalCapacity;


    //update newTotalStored
    newTotalStored = diffIOPS(newTotalStored, availableToDistribute);

    // Cap at MAX_STORAGE
    if (newTotalStored.total > QUEUE_MAX_STORAGE) {
      const ratio = QUEUE_MAX_STORAGE / newTotalStored.total;
      newTotalStored = multiplyIOPS(newTotalStored, ratio);
    }

    // We must update the actual block in the state so it persists to the next tick
    computedState = { ...computedState, blocks: { ...computedState.blocks, [currentBlockID]: { ...computedState.blocks[currentBlockID], dynamicProperties: { ...computedState.blocks[currentBlockID].dynamicProperties, queueState: newTotalStored } } } };
    totalStoredIOPS = newTotalStored;

  }

  //set the output IOPS for this block
  computedState = { ...computedState, blockIOPS: { ...computedState.blockIOPS, [currentBlockID]: { ...computedState.blockIOPS[currentBlockID], outputIOPS: availableToDistribute } } }



  //Distribute the IOPS to the next blocks
  let nextBlockIOPS: { [blockID: string]: IOPS } = {};

  if (computedState.blocks[currentBlockID].blockType === BlockTypes.LOAD_BALANCER || computedState.blocks[currentBlockID].blockType === BlockTypes.NETWORK_LOAD_BALANCER) {
    nextBlockIOPS = distributeLoadBalancerIOPS(
      computedState.blocks[currentBlockID],
      nextBlocks,
      availableToDistribute,
      computedState.trafficRoutes
    );
  } else {
    nextBlockIOPS = distributeIOPSToBlocks(nextBlocks, availableToDistribute);
  }

  //recursively call this function for each output block
  let totalPercentSuccess = 0;
  nextBlockKeys.forEach((nextBlockID: string) => {
    const dividedIOPS = nextBlockIOPS[nextBlockID];
    const blockResult = computeNextIOPSForBlock(nextBlockID, computedState, dividedIOPS, seenBlocks)
    computedState = blockResult.gameState
    totalPercentSuccess += computedState.blockIOPS[nextBlockID].percentageSuccess
  })

  //calculate the average percent success of children blocks
  const childrenPercentSuccess = nextBlockKeys.length > 0 ? Math.round(totalPercentSuccess / nextBlockKeys.length) : 100;

  //calculate the total IOPS allocated in nextBlockIOPS
  let totalAllocatedIOPS = Object.keys(nextBlockIOPS).reduce((acc, blockID) => {
    const blockIOPS = nextBlockIOPS[blockID];
    if (blockIOPS) {
      return acc + blockIOPS.total;
    }
    return acc;
  }, 0);

  totalAllocatedIOPS += totalStoredIOPS.total;


  //calculatge final percent success based on the childrenPercentSuccess and the inputCapacitySuccess
  //if the inputCapacitySuccess is 100%, then the percentSuccess is the same as the childrenPercentSuccess
  //otherwise, weight the percentSuccess by on the inputCapacitySuccess

  const overflowFraction = remainingIOPS.total > 0 ? 1 - (totalAllocatedIOPS / remainingIOPS.total) : 0;
  const childrenSuccessWeight = 1 - overflowFraction;
  const percentSuccess = childrenPercentSuccess * childrenSuccessWeight;

  computedState = {
    ...computedState, blockIOPS: {
      ...computedState.blockIOPS, [currentBlockID]: {
        ...computedState.blockIOPS[currentBlockID], percentageSuccess: percentSuccess
      }
    }
  }

  return { percentSuccess: childrenPercentSuccess, gameState: computedState };
}

/*Computes the next set of IOPS values to be set at the root block
This will then be propagated to the rest of the block tree */
function getNextRootIOPS(
  currentLevel: number,
  nextLevel: number,
  currentIOPS: IOPS,
  iopsScale: number,
  lastPercentSuccess: number
): IOPS {

  //Disable level scaling for now
  //const nextLevelScale = getNextLevelIOPSScale(currentLevel, nextLevel, lastPercentSuccess)

  let newIOPS = { ...currentIOPS };
  let IOPtotal = 0;
  //loop over all operation types
  Object.keys(newIOPS.byOperationType).forEach((opTypeKey: string) => {
    const opType = newIOPS.byOperationType[`${opTypeKey}`];
    if (opType) {
      let contentTypeTotal = 0;
      //loop over all content types
      Object.keys(opType.byContentType).forEach((contentTypeKey: string) => {
        //get current request count
        const contentTypeIOP = opType.byContentType[contentTypeKey];
        if (contentTypeIOP) {

          let nextVal = contentTypeIOP;

          //scale next value by IOPS
          //Disable level scaling for now
          //nextVal = nextVal * iopsScale * nextLevelScale;
          nextVal = nextVal * iopsScale;

          //increment content level total
          contentTypeTotal += nextVal;

          //update content level counts
          const nextContentIOP: ContentIOPSCountMap = {
            ...newIOPS.byOperationType[`${opTypeKey}`].byContentType,
            [`${contentTypeKey}`]: nextVal,
          };

          newIOPS = {
            ...newIOPS,
            byOperationType: {
              ...newIOPS.byOperationType,
              [`${opTypeKey}`]: {
                ...newIOPS.byOperationType[`${opTypeKey}`],
                byContentType: nextContentIOP,
              },
            },
          };
        }
      });
      //update content level total
      newIOPS = {
        ...newIOPS,
        byOperationType: {
          ...newIOPS.byOperationType,
          [`${opTypeKey}`]: {
            ...newIOPS.byOperationType[`${opTypeKey}`],
            total: contentTypeTotal,
          },
        },
      };

      //increment IOP level total
      IOPtotal += contentTypeTotal;
    }
  });

  //update IOP level total
  newIOPS = {
    ...newIOPS,
    total: IOPtotal,
  };
  return newIOPS;
}

//A function that iterates over all blocks and calculates the total costPerCycle
export function getTotalCostPerCycle(gameState: GameState) {
  let totalCostPerCycle = 0;
  Object.keys(gameState.blocks).forEach((blockID: string) => {
    const block = gameState.blocks[blockID];
    if (block) {
      const blockConfig = blockConfigs[block.blockType];
      if (blockConfig) {
        totalCostPerCycle += blockConfig.costPerCycle;
      }
    }
  });

  return totalCostPerCycle;
}


// export function getNextLevelIOPSScale(currentLevel: number, nextLevel: number, lastPercentSuccess: number = 100) {
//   if (currentLevel === nextLevel) return 1;
//   //Disabling backpressure for now
//   // if (lastPercentSuccess < 30) return 0.1;
//   // if (lastPercentSuccess < 50) return 0.5;
//   // if (lastPercentSuccess < 75) return 0.75;
//   return 2;
// }

//computes the CPM for a given IOPS value
export function getEarnedCPM(totalIOPS: number) {
  if (totalIOPS <= 0) return 0;
  return (totalIOPS / 1000 * DEFAULT_CPM);
}

//divides the IOPS object by the given divisor
export function divideIOPS(iops: IOPS, divisor: number): IOPS {
  const result: IOPS = {
    ...zeroIOPS(),
    total: iops.total / divisor
  };

  //loop over all operation types and divide each value by the divisor
  Object.keys(iops.byOperationType).forEach((opTypeKey: string) => {
    const opType = iops.byOperationType[`${opTypeKey}`];
    if (opType) {
      //loop over all content types
      let operationTotal = 0;
      Object.keys(opType.byContentType).forEach((contentTypeKey: string) => {
        //get current request count
        const contentTypeIOP = opType.byContentType[contentTypeKey];
        if (contentTypeIOP) {
          //divide the value by the divisor
          const nextVal = contentTypeIOP / divisor;
          //update content level counts
          const nextContentIOP: ContentIOPSCountMap = {
            ...result.byOperationType[`${opTypeKey}`].byContentType,
            [`${contentTypeKey}`]: nextVal,
          };

          result.byOperationType[`${opTypeKey}`] = {
            ...result.byOperationType[`${opTypeKey}`],
            byContentType: nextContentIOP,
          };

          operationTotal += nextVal;
        }
      });

      //update operation level total
      result.byOperationType[`${opTypeKey}`] = {
        ...result.byOperationType[`${opTypeKey}`],
        total: operationTotal,
      };
    }
  });



  return result
}

//finds the difference between two IOPS objects
export function diffIOPS(iops1: IOPS, iops2: IOPS): IOPS {
  const iopsTotal = iops1.total - iops2.total > 0 ? iops1.total - iops2.total : 0
  let result: IOPS = {
    ...zeroIOPS(),
    total: iopsTotal
  };

  //loop over all operation types
  if (iops1.byOperationType === undefined || iops2.byOperationType === undefined) {
    return result
  }
  Object.keys(iops1.byOperationType).forEach((opTypeKey: string) => {
    let operationTotal = 0;
    const opType = iops1.byOperationType[`${opTypeKey}`];
    if (opType && opType.byContentType) {
      //loop over all content types
      Object.keys(opType.byContentType).forEach((contentTypeKey: string) => {
        //get current request count
        const contentTypeIOP = opType.byContentType[contentTypeKey];
        if (contentTypeIOP !== undefined && contentTypeIOP !== null) {
          //get the same content type from the second IOPS object
          const opType2 = iops2.byOperationType[`${opTypeKey}`]
          if (opType2 && opType2.byContentType) {
            const contentTypeIOP2 = opType2
              .byContentType[contentTypeKey];
            if (contentTypeIOP2 !== undefined && contentTypeIOP2 !== null) {
              //add the two values together
              const nextVal = contentTypeIOP - contentTypeIOP2 > 0 ? contentTypeIOP - contentTypeIOP2 : 0;
              //update content level counts
              const nextContentIOP: ContentIOPSCountMap = {
                ...result.byOperationType[`${opTypeKey}`].byContentType,
                [`${contentTypeKey}`]: nextVal,
              };

              result.byOperationType[`${opTypeKey}`] = {
                ...result.byOperationType[`${opTypeKey}`],
                byContentType: nextContentIOP,
              };

              operationTotal += nextVal;
            }
          }

        }
      });

      //update operation level total
      result.byOperationType[`${opTypeKey}`] = {
        ...result.byOperationType[`${opTypeKey}`],
        total: operationTotal,
      };
    }
  })


  return result
}

//Sums together two IOPS objects
export function sumIOPS(iops1: IOPS, iops2: IOPS): IOPS {
  const result: IOPS = {
    ...zeroIOPS(),
    total: iops1.total + iops2.total
  };

  //loop over all operation types
  Object.keys(iops1.byOperationType).forEach((opTypeKey: string) => {
    const opType = iops1.byOperationType[`${opTypeKey}`];
    if (opType) {
      //loop over all content types
      let operationTotal = 0;
      Object.keys(opType.byContentType).forEach((contentTypeKey: string) => {
        //get current request count
        const contentTypeIOP = opType.byContentType[contentTypeKey];
        if (contentTypeIOP !== undefined && contentTypeIOP !== null) {
          //get the same content type from the second IOPS object
          const contentTypeIOP2 = iops2.byOperationType[`${opTypeKey}`]
            .byContentType[contentTypeKey];
          if (contentTypeIOP2 !== undefined && contentTypeIOP2 !== null) {
            //add the two values together
            const nextVal = contentTypeIOP + contentTypeIOP2;
            //update content level counts
            const nextContentIOP: ContentIOPSCountMap = {
              ...result.byOperationType[`${opTypeKey}`].byContentType,
              [`${contentTypeKey}`]: nextVal,
            };

            result.byOperationType[`${opTypeKey}`] = {
              ...result.byOperationType[`${opTypeKey}`],
              byContentType: nextContentIOP,
            };

            operationTotal += nextVal;
          }
        }
      });

      //update operation level total
      result.byOperationType[`${opTypeKey}`] = {
        ...result.byOperationType[`${opTypeKey}`],
        total: operationTotal,
      };
    }
  })

  return result;
}

export function getIOPSSummary(iops: BlockIOPSMap, blockID: string) {
  let result: IOPSSummary = { inputTotal: 0, outputTotal: 0, percentSuccess: 100 };
  if (!iops) return result;

  const blockIOPS = iops[blockID];
  if (!blockIOPS) return result;

  result = {
    inputTotal: blockIOPS?.inputIOPS?.total || 0,
    outputTotal: blockIOPS?.outputIOPS?.total || 0,
    percentSuccess: blockIOPS.percentageSuccess
  };

  return result;
}

export function distributeIOPSToBlocks(blocks: Block[], iops: IOPS): { [blockID: string]: IOPS } {
  const distributedIOPS: { [blockID: string]: IOPS } = {};
  let remainingIOPS = JSON.parse(JSON.stringify(iops));

  blocks.forEach((block) => {

    const inputCapacity = block.inputCapacity;

    let blockIOPS = zeroIOPS();

    Object.keys(remainingIOPS.byOperationType).forEach((opTypeKey) => {
      const opType = remainingIOPS.byOperationType[opTypeKey];
      if (!opType) return;

      Object.keys(opType.byContentType).forEach((contentTypeKey) => {
        const contentTypeCapacity = inputCapacity.byOperationType[opTypeKey]?.byContentType[contentTypeKey] || 0;
        if (!contentTypeCapacity) return;

        if (!blockIOPS.byOperationType[opTypeKey]) {
          blockIOPS.byOperationType[opTypeKey] = { total: 0, byContentType: {} };
        }

        const availableIOPS = remainingIOPS.byOperationType[opTypeKey]?.byContentType[contentTypeKey] || 0;

        const allocatedIOPS = Math.min(availableIOPS, contentTypeCapacity);

        blockIOPS.byOperationType[opTypeKey].byContentType[contentTypeKey] = allocatedIOPS;
        blockIOPS.byOperationType[opTypeKey].total += allocatedIOPS;
        blockIOPS.total += allocatedIOPS;

        remainingIOPS.byOperationType[opTypeKey].byContentType[contentTypeKey] -= allocatedIOPS;
        remainingIOPS.byOperationType[opTypeKey].total -= allocatedIOPS;
        remainingIOPS.total -= allocatedIOPS;
      });
    });

    distributedIOPS[block.id] = blockIOPS;
  });
  return distributedIOPS;
}

export function distributeLoadBalancerIOPS(
  loadBalancerBlock: Block,
  nextBlocks: Block[],
  iops: IOPS,
  trafficRoutes: TrafficRoutes
): { [blockID: string]: IOPS } {
  const distributedIOPS: { [blockID: string]: IOPS } = {};
  const routeAssignments = loadBalancerBlock.dynamicProperties?.routeAssignments || {};

  // Initialize distributedIOPS for all nextBlocks
  nextBlocks.forEach((block) => {
    distributedIOPS[block.id] = zeroIOPS();
  });

  const distributeEvenly = (amountToDistribute: number, blocks: Block[], opType: string, contentType: string) => {
    let remainingAmount = amountToDistribute;
    let activeBlocks = [...blocks];

    while (remainingAmount > 0 && activeBlocks.length > 0) {
      const amountPerBlock = Math.floor(remainingAmount / activeBlocks.length) || (remainingAmount > 0 ? 1 : 0);
      if (amountPerBlock === 0) break; // prevent infinite loop if amount < number of blocks

      const nextActiveBlocks: Block[] = [];
      let distributedInRound = 0;

      for (let i = 0; i < activeBlocks.length; i++) {
        if (remainingAmount <= 0) break;
        const block = activeBlocks[i];
        const capacity = block.inputCapacity.byOperationType[opType]?.byContentType[contentType] || 0;
        const currentAssigned = distributedIOPS[block.id].byOperationType[opType]?.byContentType[contentType] || 0;
        const room = Math.max(0, capacity - currentAssigned);

        const allocation = Math.min(amountPerBlock, room, remainingAmount);
        if (allocation > 0) {
          if (!distributedIOPS[block.id].byOperationType[opType]) {
            distributedIOPS[block.id].byOperationType[opType] = { total: 0, byContentType: {} }
          }
          distributedIOPS[block.id].byOperationType[opType].byContentType[contentType] = (distributedIOPS[block.id].byOperationType[opType]?.byContentType[contentType] || 0) + allocation;
          distributedIOPS[block.id].byOperationType[opType].total += allocation;
          distributedIOPS[block.id].total += allocation;
          distributedInRound += allocation;
          remainingAmount -= allocation;

          if (allocation < room) { // still has room
            nextActiveBlocks.push(block);
          }
        }
      }

      activeBlocks = nextActiveBlocks;
      if (distributedInRound === 0) break;
    }
  };

  const totalRouteWeight = trafficRoutes.reduce((sum, r) => sum + r.weight, 0);

  const opTypes = [OperationTypes.READ, OperationTypes.WRITE];
  const contentTypes = [ContentTypes.STATIC, ContentTypes.DYNAMIC, ContentTypes.STATIC_MEDIA];

  opTypes.forEach((opType) => {
    contentTypes.forEach((contentType) => {
      const availableTotal = iops.byOperationType[opType]?.byContentType[contentType] || 0;
      if (availableTotal <= 0) return;

      if (totalRouteWeight <= 0) {
        distributeEvenly(availableTotal, nextBlocks, opType, contentType);
        return;
      }

      // Calculate traffic weights for all routes for this specific (contentType, opType) combination
      const routeTrafficWeights = trafficRoutes.map(route => {
        const routeWeightFraction = route.weight / totalRouteWeight;
        const ctConfig = route.contentTypes.find(ct => ct.type === contentType);
        if (!ctConfig) {
          return { route, weight: 0 };
        }

        const totalCTWeight = route.contentTypes.reduce((sum, ct) => sum + ct.weight, 0);
        const ctWeightFraction = totalCTWeight > 0 ? ctConfig.weight / totalCTWeight : 0;

        let opFraction = 0;
        if (opType === OperationTypes.READ) {
          opFraction = ctConfig.readRatio / 100;
        } else {
          opFraction = (100 - ctConfig.readRatio) / 100;
        }

        return { route, weight: routeWeightFraction * ctWeightFraction * opFraction };
      });

      const totalTrafficWeightForCategory = routeTrafficWeights.reduce((sum, rw) => sum + rw.weight, 0);

      if (totalTrafficWeightForCategory <= 0) {
        distributeEvenly(availableTotal, nextBlocks, opType, contentType);
        return;
      }

      let distributedIOPSCount = 0;
      let unassignedIOPS = 0;

      routeTrafficWeights.forEach(({ route, weight }) => {
        if (weight <= 0) return;
        const routeAllocated = Math.floor((weight / totalTrafficWeightForCategory) * availableTotal);
        if (routeAllocated <= 0) return;

        const assignmentKey = `${route.id}_${contentType}_${opType}`;
        const assignedTargetId = routeAssignments[assignmentKey];
        const targetBlock = nextBlocks.find(b => b.id === assignedTargetId);

        if (assignedTargetId && targetBlock) {
          const capacity = targetBlock.inputCapacity.byOperationType[opType]?.byContentType[contentType] || 0;
          const currentAssigned = distributedIOPS[targetBlock.id].byOperationType[opType]?.byContentType[contentType] || 0;
          const room = Math.max(0, capacity - currentAssigned);

          const actualAllocation = Math.min(routeAllocated, room);

          if (!distributedIOPS[targetBlock.id].byOperationType[opType]) {
            distributedIOPS[targetBlock.id].byOperationType[opType] = { total: 0, byContentType: {} };
          }

          distributedIOPS[targetBlock.id].byOperationType[opType].byContentType[contentType] =
            (distributedIOPS[targetBlock.id].byOperationType[opType]?.byContentType[contentType] || 0) + actualAllocation;
          distributedIOPS[targetBlock.id].byOperationType[opType].total += actualAllocation;
          distributedIOPS[targetBlock.id].total += actualAllocation;
          distributedIOPSCount += actualAllocation;

          if (routeAllocated > room) {
            unassignedIOPS += (routeAllocated - room);
          }
        } else {
          unassignedIOPS += routeAllocated;
        }
      });

      const roundingLeftover = availableTotal - (distributedIOPSCount + unassignedIOPS);
      if (roundingLeftover > 0) {
        unassignedIOPS += roundingLeftover;
      }

      if (unassignedIOPS > 0 && nextBlocks.length > 0) {
        distributeEvenly(unassignedIOPS, nextBlocks, opType, contentType);
      }
    });
  });

  return distributedIOPS;
}

// function testDistribution() {
//   const blocks: Block[] = [
//     {
//       top: 0,
//       left: 0,
//       id: 'block1',
//       width: 100,
//       height: 100,
//       blockType: BlockTypes.APP_SERVER,
//       inputCapacity: zeroIOPS(),
//       processingCapacity: zeroIOPS(),
//     },
//     {
//       top: 0,
//       left: 0,
//       id: 'block2',
//       width: 100,
//       height: 100,
//       blockType: BlockTypes.CDN,
//       inputCapacity: zeroIOPS(),
//       processingCapacity: zeroIOPS(),
//     },
//   ];

//   const iops: IOPS = {
//     total: 10900,
//     byOperationType: {
//       [`${OperationTypes.READ}`]: {
//         total: 10600,
//         byContentType: {
//           [`${ContentTypes.STATIC}`]: 10300,
//           [`${ContentTypes.DYNAMIC}`]: 300,
//         },
//       },
//       [`${OperationTypes.WRITE}`]: {
//         total: 300,
//         byContentType: {
//           [`${ContentTypes.DYNAMIC}`]: 300,
//         },
//       },
//     },
//   };

export function ensureNodeBlockTypes(state: GameState): GameState {
  if (!state || !state.nodes || !state.blocks) return state;

  // 1. Ensure trafficRoutes is in new array format and static content type has readRatio: 100
  let currentRoutes = state.trafficRoutes;
  if (!currentRoutes || !Array.isArray(currentRoutes)) {
    currentRoutes = defaultTrafficRoutes;
  } else {
    currentRoutes = currentRoutes.map(route => ({
      ...route,
      contentTypes: route.contentTypes.map(ct =>
        ct.type === ContentTypes.STATIC ? { ...ct, readRatio: 100 } : ct
      )
    }));
  }

  // 2. Ensure all blocks have static_media initialized in capacities
  const updatedBlocks = state.blocks ? { ...state.blocks } : {};
  Object.keys(updatedBlocks).forEach((blockId) => {
    const block = updatedBlocks[blockId];
    if (block) {
      const ensureStaticMedia = (iops: IOPS): IOPS => {
        if (!iops) return zeroIOPS();
        const res = {
          ...iops,
          byOperationType: { ...iops.byOperationType },
        };
        [OperationTypes.READ, OperationTypes.WRITE].forEach((op) => {
          if (!res.byOperationType[op]) {
            res.byOperationType[op] = { total: 0, byContentType: {} };
          } else {
            res.byOperationType[op] = {
              ...res.byOperationType[op],
              byContentType: { ...res.byOperationType[op].byContentType },
            };
          }
          if (res.byOperationType[op].byContentType[ContentTypes.STATIC_MEDIA] === undefined) {
            res.byOperationType[op].byContentType[ContentTypes.STATIC_MEDIA] = 0;
          }
        });
        return res;
      };

      updatedBlocks[blockId] = {
        ...block,
        inputCapacity: ensureStaticMedia(block.inputCapacity),
        processingCapacity: ensureStaticMedia(block.processingCapacity),
        maxProcessingCapacity: block.maxProcessingCapacity ? ensureStaticMedia(block.maxProcessingCapacity) : undefined,
      };
    }
  });

  // 3. Ensure nodes have blockType
  let updatedNodes = state.nodes || [];
  if (state.blocks) {
    updatedNodes = updatedNodes.map(node => {
      const block = state.blocks[node.id];
      if (block && (!node.data || !node.data.blockType)) {
        return {
          ...node,
          data: {
            ...node.data,
            blockType: block.blockType,
          }
        };
      }
      return node;
    });
  }

  // 4. Ensure initialRootIOPS and root block outputIOPS have static_media initialized
  const ensureStaticMedia = (iops: IOPS): IOPS => {
    if (!iops) return zeroIOPS();
    const res = {
      ...iops,
      byOperationType: { ...iops.byOperationType },
    };
    [OperationTypes.READ, OperationTypes.WRITE].forEach((op) => {
      if (!res.byOperationType[op]) {
        res.byOperationType[op] = { total: 0, byContentType: {} };
      } else {
        res.byOperationType[op] = {
          ...res.byOperationType[op],
          byContentType: { ...res.byOperationType[op].byContentType },
        };
      }
      if (res.byOperationType[op].byContentType[ContentTypes.STATIC_MEDIA] === undefined) {
        res.byOperationType[op].byContentType[ContentTypes.STATIC_MEDIA] = 0;
      }
    });
    return res;
  };

  const initialRootIOPS = ensureStaticMedia(state.initialRootIOPS);
  const blockIOPS = state.blockIOPS ? { ...state.blockIOPS } : {};
  Object.keys(blockIOPS).forEach((id) => {
    blockIOPS[id] = {
      ...blockIOPS[id],
      inputIOPS: ensureStaticMedia(blockIOPS[id].inputIOPS),
      outputIOPS: ensureStaticMedia(blockIOPS[id].outputIOPS),
    };
  });

  return {
    ...state,
    trafficRoutes: currentRoutes,
    blocks: updatedBlocks,
    nodes: updatedNodes,
    initialRootIOPS,
    blockIOPS,
    isDraggingNode: state.isDraggingNode ?? false,
  };
}
